import React from 'react';
import useAutoFocusFirstField from 'hooks/useAutoFocusFirstField';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';

import { Button, IconButton, TextField } from '@material-ui/core';

import { Add, Delete } from '@material-ui/icons';

import ConfirmDialog from 'components/ConfirmDialog';
import SelectField from 'components/SelectField';

import { mapToSelectOptions } from 'utils/select';
import { getApiErrorMessage } from 'utils/errors';

import { getVenuesWithStockModuleActive } from 'services/venuesServices';
import { getVenueGlobalProducts } from 'services/stockServices';
import { getStockRecipe, updateStockRecipe } from 'services/stockRecipeServices';

import Venue from 'types/models/Venue';
import { GlobalProduct, StockProductType } from 'types/models/StockReceipt';
import { useHistory } from 'react-router-dom';
import { StockRecipe } from 'types/models/StockMovement';
import { StockRecipeFormSchema } from 'forms/schemas/StockModuleForm';
import SaveError from 'components/common/SaveError';

import AppContext from 'contexts/AppContext';
import useSetupInitialState from 'hooks/useSetupInitialState';
import UserRoleType from 'types/enums/UserRoleType';

interface Props {
  recipe?: StockRecipe;
}

interface RecipeIngredient {
  globalProductId: number;
  quantity: number;
}

export default ({ recipe }: Props): JSX.Element => {
  const history = useHistory();
  const { t } = useTranslation();

  const { authData } = React.useContext(AppContext);
  const entity = useSetupInitialState();
  const isVenueEditor = authData?.user?.role === UserRoleType.VenueEditor;

  const [venues, setVenues] = React.useState<Venue[]>([]);

  const [finishedProducts, setFinishedProducts] = React.useState<GlobalProduct[]>([]);
  const [rawMaterials, setRawMaterials] = React.useState<GlobalProduct[]>([]);

  const [ingredients, setIngredients] = React.useState<RecipeIngredient[]>([{ globalProductId: 0, quantity: 1 }]);

  const [openDialog, setOpenDialog] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const formRef = useAutoFocusFirstField(true);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: recipe?.id ?? '',
      venueId: recipe?.globalProduct?.venueId ?? entity?.id ?? '',
      globalProductId: recipe?.globalProductId ?? '',
    },
    validationSchema: () => StockRecipeFormSchema(t),
    onSubmit: () => setOpenDialog(true),
  });

  React.useEffect(() => {
    if (isVenueEditor) return;

    async function loadVenues() {
      try {
        const data = await getVenuesWithStockModuleActive();
        setVenues(data?.data ?? []);
      } catch (error) {
        console.error(error);
      }
    }

    loadVenues();
  }, [isVenueEditor]);

  React.useEffect(() => {
    if (!formik.values.venueId) {
      setFinishedProducts([]);
      setRawMaterials([]);
      return;
    }

    async function loadProducts() {
      try {
        const [finished, raw] = await Promise.all([
          getVenueGlobalProducts(formik.values.venueId, {
            simple: true,
            type: StockProductType.FINISHED,
            excludeWithRecipe: true,
          }),
          getVenueGlobalProducts(formik.values.venueId, {
            simple: true,
            type: StockProductType.RAW_MATERIAL,
          }),
        ]);

        setFinishedProducts(finished);
        setRawMaterials(raw);
      } catch (error) {
        console.error(error);
      }
    }

    loadProducts();

    if (!recipe) {
      formik.setFieldValue('globalProductId', '');
      setIngredients([{ globalProductId: 0, quantity: 1 }]);
    }
  }, [formik.values.venueId]);

  React.useEffect(() => {
    if (!recipe) return;
    if (!formik.values.globalProductId) return;

    async function loadRecipe() {
      try {
        const data = await getStockRecipe(formik.values.id);

        if (data.items?.length) {
          setIngredients(
            data.items.map((item) => ({
              globalProductId: +item.ingredientGlobalProductId,
              quantity: +item.quantity,
            })),
          );
        } else {
          setIngredients([{ globalProductId: 0, quantity: 1 }]);
        }
      } catch (error) {
        console.error(error);
      }
    }

    loadRecipe();
  }, [formik.values.globalProductId]);

  const updateIngredient = (index: number, field: keyof RecipeIngredient, value: any) => {
    setIngredients((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        return { ...item, [field]: value };
      }),
    );
  };

  const addIngredient = () => {
    setIngredients((prev) => [
      ...prev,
      {
        globalProductId: 0,
        quantity: 1,
      },
    ]);
  };

  const removeIngredient = (index: number) => {
    setIngredients((prev) => prev.filter((_, i) => i !== index));
  };

  const onConfirm = async () => {
    setSaveError(null);

    try {
      await updateStockRecipe(+formik.values.globalProductId, {
        ingredients: ingredients.map(({ globalProductId, quantity }) => ({
          globalProductId,
          quantity,
        })),
      });

      history.goBack();
    } catch (error) {
      setSaveError(getApiErrorMessage(error) ?? t('errors.generic'));
    } finally {
      setOpenDialog(false);
      formik.setSubmitting(false);
    }
  };

  const finishedProductOptions = mapToSelectOptions(finishedProducts, {
    value: 'id',
    label: 'name',
  });

  // En edición, el producto terminado actual ya tiene receta (queda excluido del listado):
  // se agrega a las opciones para que el select muestre su nombre.
  if (recipe?.globalProduct && !finishedProducts.some((product) => product.id === recipe.globalProduct?.id)) {
    finishedProductOptions.push({
      value: recipe.globalProduct.id,
      label: recipe.globalProduct.name,
    });
  }

  return (
    <>
      <form onSubmit={formik.handleSubmit} ref={formRef}>
        <h2>Receta</h2>

        {!isVenueEditor && (
          <SelectField
            name="venueId"
            label="Venue"
            placeholder="Seleccione"
            value={formik.values.venueId}
            options={mapToSelectOptions(venues, {
              label: 'name',
            })}
            isMulti={false}
            setFieldValue={formik.setFieldValue}
            onBlur={formik.handleBlur}
            touched={formik.touched.venueId}
            error={formik.errors.venueId}
            isDisabled={formik.isSubmitting || Boolean(recipe)}
            isClearable
            isSearchable
            backspaceRemovesValue
          />
        )}

        <SelectField
          name="globalProductId"
          label="Producto terminado"
          placeholder="Seleccione"
          value={formik.values.globalProductId}
          options={finishedProductOptions}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.globalProductId}
          error={formik.errors.globalProductId}
          isDisabled={formik.isSubmitting || !formik.values.venueId || Boolean(recipe)}
          isClearable
          isSearchable
          backspaceRemovesValue
        />

        <h3>Ingredientes</h3>

        {ingredients.map((ingredient, index) => {
          const ingredientProduct = rawMaterials.find((product) => product.id === ingredient.globalProductId);

          return (
            <div
              key={index}
              style={{
                border: '1px solid #DDD',
                padding: 15,
                marginBottom: 15,
              }}
            >
              <SelectField
                name={`ingredient-${index}`}
                label="Materia prima"
                placeholder="Seleccione"
                value={ingredient.globalProductId}
                options={mapToSelectOptions(rawMaterials, {
                  value: 'id',
                  label: 'name',
                })}
                isMulti={false}
                setFieldValue={(_, value) => updateIngredient(index, 'globalProductId', value)}
                touched={false}
                error={false}
                isDisabled={formik.isSubmitting || !formik.values.globalProductId}
                isClearable
                isSearchable
                backspaceRemovesValue
                onBlur={() => undefined}
              />

              <TextField
                margin="dense"
                fullWidth
                type="number"
                label="Cantidad"
                value={ingredient.quantity}
                onChange={(e) => updateIngredient(index, 'quantity', Number(e.target.value))}
                inputProps={{
                  min: 0.0001,
                  step: 0.0001,
                }}
              />

              <p style={{ marginTop: 4, marginBottom: 0, color: '#666', fontSize: 13 }}>
                {ingredientProduct?.unitMeasure
                  ? `${t('stock.units.unit-label')}: ${t(`stock.units.${ingredientProduct.unitMeasure}`)}`
                  : t('stock.recipes.form.unit-note')}
              </p>

              <div
                style={{
                  marginTop: 10,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div />

                <div>
                  <IconButton color="primary" onClick={addIngredient}>
                    <Add />
                  </IconButton>

                  {ingredients.length > 1 && (
                    <IconButton color="secondary" onClick={() => removeIngredient(index)}>
                      <Delete />
                    </IconButton>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        <SaveError error={saveError} />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={formik.isSubmitting || !formik.values.venueId || !formik.values.globalProductId}
        >
          Guardar receta
        </Button>
      </form>

      <ConfirmDialog
        open={openDialog}
        setOpen={setOpenDialog}
        onConfirm={onConfirm}
        onCancel={() => formik.setSubmitting(false)}
      />
    </>
  );
};
